import { useId } from 'react';
import {
  surface,
  option,
  optionActive,
  icon as iconCls,
  label as labelCls,
  match,
} from './mention.css';
import type { ComposerMentionItem } from './mention';

export interface MentionListProps {
  items: readonly ComposerMentionItem[];
  activeIndex: number;
  /** The typed prefix, bolded within each label. */
  query: string;
  /** Accessible label for the listbox. */
  listLabel: string;
  onHover: (index: number) => void;
  onSelect: (index: number) => void;
}

/**
 * MentionList — the @mention suggestion surface: a `role="listbox"` styled from
 * Genesis tokens (mirrors the DS Menu/Popover). Presentational only — the
 * Composer's Lexical typeahead plugin owns detection, keyboard, and positioning
 * (it portals this into a caret-anchored element). Internal to `Composer`.
 */
export function MentionList({
  items,
  activeIndex,
  query,
  listLabel,
  onHover,
  onSelect,
}: MentionListProps) {
  const baseId = useId();
  const optionId = (i: number) => `${baseId}-opt-${i}`;

  return (
    <ul
      role="listbox"
      aria-label={listLabel}
      className={surface}
      // Keep focus in the editor when a row is clicked.
      onMouseDown={(e) => e.preventDefault()}
    >
      {items.map((item, i) => {
        const matched = item.label.slice(0, query.length);
        const rest = item.label.slice(query.length);
        return (
          <li key={item.id} role="none">
            <button
              id={optionId(i)}
              role="option"
              type="button"
              aria-label={item.label}
              aria-selected={i === activeIndex}
              tabIndex={-1}
              className={`${option} ${i === activeIndex ? optionActive : ''}`}
              onMouseEnter={() => onHover(i)}
              onClick={() => onSelect(i)}
            >
              {item.icon != null && (
                <span className={iconCls} aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <span className={labelCls}>
                {matched && <span className={match}>{matched}</span>}
                {rest}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
MentionList.displayName = 'MentionList';
